import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { format, isWithinInterval, addHours } from 'date-fns';

export default function FollowUps() {
  const { user } = useAuth();
  const [followups, setFollowups] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  const fetchFollowups = async () => {
    setLoading(true);
    try {
      const endpoint = user?.role === 'superadmin' ? '/followups/all' : '/followups/my';
      const { data } = await api.get(endpoint);
      setFollowups(data);
    } catch (e) {
      toast.error('Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFollowups(); }, [user]);

  const markDone = async (id) => {
    try {
      await api.put(`/followups/${id}`, { status: 'done' });
      toast.success('Marked as done');
      fetchFollowups();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const isDueSoon = (scheduledAt) => {
    const now = new Date();
    const oneHourLater = addHours(now, 1);
    try {
      return isWithinInterval(new Date(scheduledAt), { start: now, end: oneHourLater });
    } catch (e) {
      return false;
    }
  };

  const tabs = ['pending', 'done', 'missed'];
  const filtered = filter === 'all' ? followups : followups.filter(f => f.status === filter);

  return (
    <div className="fadein">
      <div className="page-header">
        <div>
          <div className="page-title">Follow-ups</div>
          <div className="page-subtitle">{followups.length} total</div>
        </div>
      </div>

      <div className="tabs">
        {tabs.map(t => (
          <button
            key={t}
            className={`tab ${filter === t ? 'active' : ''}`}
            onClick={() => setFilter(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
            <span style={{ marginLeft: 5, fontSize: 11, opacity: 0.7 }}>
              ({followups.filter(f => f.status === t).length})
            </span>
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="empty"><div className="empty-icon"></div><p>Loading...</p></div>
        ) : filtered.length === 0 ? (
          <div className="empty"><div className="empty-icon"></div><p>No follow-ups</p></div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Lead Name</th>
                <th>Phone</th>
                <th>Scheduled At</th>
                <th>Notes</th>
                <th>Status</th>
                {user?.role === 'superadmin' && <th>Employee</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(f => {
                const dueSoon = f.status === 'pending' && isDueSoon(f.scheduledAt);
                return (
                  <tr key={f._id} style={dueSoon ? { background: 'var(--amber-dim)' } : {}}>
                    <td style={{ fontWeight: 500 }}>
                      {dueSoon && <span style={{ marginRight: 6, fontSize: 12 }}>!</span>}
                      {f.lead?.name || '—'}
                    </td>
                    <td style={{ color: 'var(--text-3)', fontSize: 13 }}>{f.lead?.number || '—'}</td>
                    <td style={{ fontSize: 13 }}>
                      {f.scheduledAt ? format(new Date(f.scheduledAt), 'MMM d, yyyy h:mm a') : '—'}
                    </td>
                    <td style={{ color: 'var(--text-3)', fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.notes || '—'}
                    </td>
                    <td><StatusBadge status={f.status} /></td>
                    {user?.role === 'superadmin' && (
                      <td style={{ fontSize: 13 }}>{f.user?.name || '—'}</td>
                    )}
                    <td>
                      {f.status === 'pending' && (
                        <button className="btn btn-xs btn-success" onClick={() => markDone(f._id)}>
                          ✓ Done
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: 'badge-pending',
    done:    'badge-done',
    missed:  'badge-missed'
  };
  return <span className={`badge ${map[status] || 'badge-pending'}`}>{status}</span>;
}
